import React, { useState } from 'react';

const HtmlImport = ({ onImportComplete }) => {
  const [htmlContent, setHtmlContent] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);

  const htmlTemplate = `<!-- Fish data structure example -->
<div class="fish-item">
  <h3 class="fish-name">Mandarin Dragonet</h3>
  <p class="scientific-name">Synchiropus splendidus</p>
  <span class="price">$89.99</span>
  <p class="description">Beautiful psychedelic colors with intricate patterns</p>
  <span class="care-level">intermediate</span>
  <span class="size">3 inches</span>
  <span class="temperament">Peaceful</span>
  <span class="reef-safe">true</span>
  <span class="diet">Carnivore</span>
  <span class="origin">Pacific Ocean</span>
  <span class="category">Dragonet</span>
  <span class="tank-size">30+ gal</span>
  <span class="stock">3</span>
  <span class="available">true</span>
  <img class="fish-image" src="https://images.unsplash.com/photo-1544551763-46a013bb70d5" alt="Mandarin Dragonet" />
</div>

<div class="fish-item">
  <h3 class="fish-name">Yellow Tang</h3>
  <p class="scientific-name">Zebrasoma flavescens</p>
  <span class="price">$79.99</span>
  <p class="description">Bright yellow algae eater perfect for beginners</p>
  <span class="care-level">beginner</span>
  <span class="size">8 inches</span>
  <span class="temperament">Peaceful</span>
  <span class="reef-safe">true</span>
  <span class="diet">Herbivore</span>
  <span class="origin">Hawaii</span>
  <span class="category">Tang</span>
  <span class="tank-size">75+ gal</span>
  <span class="stock">5</span>
  <span class="available">true</span>
  <img class="fish-image" src="https://images.unsplash.com/photo-1559827260-dc66d52bef19" alt="Yellow Tang" />
</div>`;

  const downloadTemplate = () => {
    const blob = new Blob([htmlTemplate], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'fish_template.html';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === 'text/html' || file.name.endsWith('.html'))) {
      setSelectedFile(file);
      readFileContent(file);
    } else {
      alert('Please select an HTML file (.html)');
    }
  };

  const readFileContent = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setHtmlContent(content);
      setImportResults(null); // Clear previous results
    };
    reader.onerror = () => {
      alert('Error reading file');
    };
    reader.readAsText(file);
  };

  const triggerFileInput = () => {
    const fileInput = document.getElementById('html-file-input');
    fileInput.click();
  };

  const parseHTML = (htmlString) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    
    // Try to parse Marine Fish Care Guide format first
    const careCards = doc.querySelectorAll('.care-card');
    if (careCards.length > 0) {
      return parseCareGuideFormat(careCards);
    }
    
    // Fallback to original fish-item format
    const fishItems = doc.querySelectorAll('.fish-item');
    return parseStandardFormat(fishItems);
  };

  const parseCareGuideFormat = (careCards) => {
    const results = [];

    careCards.forEach((card, index) => {
      try {
        // Extract basic info from header
        const header = card.querySelector('header');
        if (!header) {
          console.warn(`No header found in card ${index}`);
          return;
        }

        const h2 = header.querySelector('h2');
        if (!h2) {
          console.warn(`No h2 found in card ${index}`);
          return;
        }

        const fullTitle = h2.textContent?.trim() || '';
        console.log(`Processing: ${fullTitle}`);

        // Extract scientific name from small tag
        const smallTag = h2.querySelector('small');
        let scientificName = '';
        if (smallTag) {
          scientificName = smallTag.textContent?.replace(/[()]/g, '').trim() || '';
        }

        // Extract common name (text before small tag or split by scientific name)
        let commonName = fullTitle;
        if (smallTag) {
          // Get text content without the small tag
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = h2.innerHTML;
          const small = tempDiv.querySelector('small');
          if (small) {
            small.remove();
          }
          commonName = tempDiv.textContent?.trim() || '';
        }

        // Clean up common name
        commonName = commonName.replace(/\s+/g, ' ').trim();
        
        const category = header.querySelector('.category')?.textContent?.trim() || 'Fish';

        // Extract tank size and adult size
        const sections = card.querySelectorAll('section');
        let tankSize = '';
        let size = '';
        let description = '';
        let diet = '';
        let temperament = 'Peaceful';

        sections.forEach(section => {
          const h3 = section.querySelector('h3');
          if (!h3) return;
          
          const sectionTitle = h3.textContent?.trim() || '';
          const sectionText = section.querySelector('p')?.textContent || '';

          if (sectionTitle.includes('Tank Size')) {
            const tankSizeMatch = sectionText.match(/(\d+)\s*L\s*\((\d+)\s*gal\)/);
            tankSize = tankSizeMatch ? `${tankSizeMatch[2]} gal` : '30 gal';
            
            const adultSizeMatch = sectionText.match(/(\d+)[–-](\d+)\s*cm|Up to (\d+)\s*cm|(\d+)\s*cm/);
            if (adultSizeMatch) {
              if (adultSizeMatch[3]) {
                size = `${adultSizeMatch[3]} cm`;
              } else if (adultSizeMatch[1] && adultSizeMatch[2]) {
                size = `${adultSizeMatch[1]}-${adultSizeMatch[2]} cm`;
              } else if (adultSizeMatch[4]) {
                size = `${adultSizeMatch[4]} cm`;
              }
            }
          } else if (sectionTitle.includes('Overview')) {
            description = sectionText.trim();
          } else if (sectionTitle.includes('Diet')) {
            diet = sectionText.split('.')[0].trim();
          } else if (sectionTitle.includes('Temperament')) {
            temperament = sectionText.trim();
          }
        });

        // Set defaults if not found
        if (!tankSize) tankSize = '40 gal';
        if (!size) size = '10 cm';
        if (!description) description = `Beautiful ${category.toLowerCase()} from Maldivian waters.`;
        if (!diet) diet = 'Omnivore';

        // Determine care level based on tank size and description
        let careLevel = 'intermediate';
        const tankGallons = parseInt(tankSize);
        if (tankGallons <= 30) {
          careLevel = 'beginner';
        } else if (tankGallons >= 75 || description.toLowerCase().includes('territorial') || description.toLowerCase().includes('aggressive')) {
          careLevel = 'expert';
        }

        // Generate realistic price based on category and size
        const basePrice = getCategoryPrice(category);
        const sizeMultiplier = getSizeMultiplier(size);
        const price = Math.round(basePrice * sizeMultiplier);

        const fishData = { // Initialize fishData object
          id: Date.now() + index, // Generate unique ID
          name: commonName || `${category} Fish`,
          scientific_name: scientificName || 'Species unknown',
          price: price,
          description: description.length > 150 ? description.substring(0, 150) + '...' : description,
          care_level: careLevel,
          size: size,
          temperament: temperament,
          reef_safe: true,
          diet: diet,
          origin: 'Maldives',
          category: category,
          tank_size: tankSize,
          stock_quantity: Math.floor(Math.random() * 5) + 1,
          is_available: true,
          image_url: `https://images.unsplash.com/photo-${1544551763 + index}?auto=format&fit=crop&w=400&h=300&q=80`,
          image: getEmojiForFish(commonName || category)
        };

        // More lenient validation - just need a name
        if (fishData.name && fishData.name.length > 2) { // Basic validation
          results.push(fishData); // Add parsed fish data to results
          console.log(`Successfully parsed: ${fishData.name} (${fishData.scientific_name})`); // Log success
        } else {
          console.warn(`Skipped fish with insufficient data: ${fishData.name}`); // Log skipped fish
        }
      } catch (error) {
        console.warn(`Error parsing fish card ${index}:`, error);
      }
    });

    console.log(`Parsed ${results.length} fish from care guide`);
    return results;
  };

  const parseStandardFormat = (fishItems) => {
    const results = [];

    fishItems.forEach((item) => { // Iterate over each fish item
      const fishData = { // Initialize fishData object
        id: Date.now() + Math.random(), // Generate unique ID
        name: item.querySelector('.fish-name')?.textContent?.trim() || '', // Extract name
        scientific_name: item.querySelector('.scientific-name')?.textContent?.trim() || '', // Extract scientific name
        price: parseFloat(item.querySelector('.price')?.textContent?.replace(/[$,]/g, '')) || 0, // Extract and parse price
        description: item.querySelector('.description')?.textContent?.trim() || '', // Extract description
        care_level: item.querySelector('.care-level')?.textContent?.trim() || 'beginner', // Extract care level
        size: item.querySelector('.size')?.textContent?.trim() || '', // Extract size
        temperament: item.querySelector('.temperament')?.textContent?.trim() || '', // Extract temperament
        reef_safe: item.querySelector('.reef-safe')?.textContent?.trim() === 'true', // Extract reef safe status
        diet: item.querySelector('.diet')?.textContent?.trim() || '', // Extract diet
        origin: item.querySelector('.origin')?.textContent?.trim() || '', // Extract origin
        category: item.querySelector('.category')?.textContent?.trim() || '', // Extract category
        tank_size: item.querySelector('.tank-size')?.textContent?.trim() || '', // Extract tank size
        stock_quantity: parseInt(item.querySelector('.stock')?.textContent?.trim()) || 1, // Extract stock quantity
        is_available: item.querySelector('.available')?.textContent?.trim() === 'true', // Extract availability
        image_url: item.querySelector('.fish-image')?.getAttribute('src') || '', // Extract image URL
        image: getEmojiForFish(item.querySelector('.fish-name')?.textContent?.trim() || '') // Generate emoji
      };

      if (fishData.name) { // Basic validation
        results.push(fishData); // Add parsed fish data to results
      }
    });

    return results;
  };

  const getCategoryPrice = (category) => {
    const categoryPrices = {
      'Anthias': 85,
      'Angelfish': 120,
      'Butterflyfish': 95,
      'Cardinalfish': 45,
      'Clownfish': 65,
      'Damselfish': 35,
      'Dottyback': 55,
      'Goby': 60,
      'Grouper': 150,
      'Hawkfish': 85,
      'Parrotfish': 110,
      'Pufferfish': 90,
      'Surgeonfish': 80,
      'Tang': 90,
      'Triggerfish': 130,
      'Wrasse': 75,
      'Eel': 160,
      'Filefish': 70,
      'Frogfish': 200,
      'Lionfish': 140
    };
    return categoryPrices[category] || 80;
  };

  const getSizeMultiplier = (size) => {
    if (!size) return 1;
    const sizeNum = parseInt(size);
    if (sizeNum <= 5) return 0.8;
    if (sizeNum <= 10) return 1.0;
    if (sizeNum <= 15) return 1.3;
    if (sizeNum <= 20) return 1.6;
    return 2.0;
  };

  const getEmojiForFish = (fishName) => {
    const name = fishName.toLowerCase();
    if (name.includes('clown')) return '🤡';
    if (name.includes('tang')) return '💙';
    if (name.includes('angel')) return '👼';
    if (name.includes('lion')) return '🦁';
    if (name.includes('dragonet')) return '🐟';
    if (name.includes('cardinal')) return '🪙';
    if (name.includes('trigger')) return '🎨';
    if (name.includes('betta')) return '🌙';
    if (name.includes('firefish')) return '💜';
    return '🐠';
  };

  const previewImport = () => {
    if (!htmlContent.trim()) {
      alert('Please paste some HTML content first');
      return;
    }

    try {
      const parsed = parseHTML(htmlContent);
      if (parsed.length === 0) {
        alert('No fish data found. Make sure to use the correct HTML structure with .fish-item class.');
        return;
      }
      setPreviewData(parsed);
      setShowPreview(true);
    } catch (error) {
      alert('Error parsing HTML: ' + error.message);
    }
  };

  const handleImport = async (confirmedData = null) => {
    if (!htmlContent.trim() && !confirmedData) return;

    setIsImporting(true);
    
    try {
      let fishToImport;
      
      if (confirmedData) {
        fishToImport = confirmedData;
      } else {
        fishToImport = parseHTML(htmlContent);
      }

      if (fishToImport.length === 0) {
        throw new Error('No valid fish data found in the HTML');
      }

      setImportResults({
        success: true,
        count: fishToImport.length,
        data: fishToImport
      });

      // Call parent callback with imported data
      if (onImportComplete) {
        onImportComplete(fishToImport);
      }

      setHtmlContent('');
      setShowPreview(false);
      
    } catch (error) {
      setImportResults({
        success: false,
        error: error.message
      });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: '20px' }}>
      <h3 style={{ 
        fontFamily: 'Playfair Display, serif',
        marginBottom: '20px',
        color: 'var(--tropical-deep)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        🌐 HTML Import
      </h3>

      {/* Template Download & File Import */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={downloadTemplate}
            className="btn btn-secondary"
            style={{ fontSize: '14px', padding: '8px 16px' }}
          >
            📥 Download Template
          </button>
          <button
            onClick={triggerFileInput}
            className="btn"
            style={{ fontSize: '14px', padding: '8px 16px' }}
          >
            📂 Import HTML File
          </button>
        </div>
        
        {/* Hidden file input */}
        <input
          id="html-file-input"
          type="file"
          accept=".html,text/html"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        {selectedFile && (
          <div style={{ 
            padding: '10px', 
            background: 'rgba(101, 223, 246, 0.1)', 
            borderRadius: '6px',
            fontSize: '14px',
            color: '#555',
            marginBottom: '10px'
          }}>
            📄 <strong>File loaded:</strong> {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
          </div>
        )}
        
        <p style={{ fontSize: '12px', color: '#666' }}>
          Download the template to see the required HTML structure or import your Marine Fish Care Guide HTML file directly.
        </p>
      </div>

      {/* HTML Input */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
          Paste HTML Content:
        </label>
        <textarea
          value={htmlContent}
          onChange={(e) => setHtmlContent(e.target.value)}
          placeholder="Paste your HTML content here with fish data..."
          rows={12}
          style={{
            width: '100%',
            padding: '12px',
            border: '2px solid var(--tropical-aqua)',
            borderRadius: 'var(--radius)',
            fontFamily: 'monospace',
            fontSize: '14px',
            background: 'rgba(0, 206, 201, 0.05)'
          }}
        />
      </div>

      {/* Actions */}
      {htmlContent.trim() && (
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button
            onClick={previewImport}
            className="btn btn-secondary"
            style={{ fontSize: '14px' }}
          >
            👁️ Preview Data
          </button>
          <button
            onClick={() => handleImport()}
            disabled={isImporting}
            className="btn"
            style={{ fontSize: '14px' }}
          >
            {isImporting ? 'Importing...' : '📤 Import Fish'}
          </button>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div className="card" style={{ 
            maxWidth: '800px', 
            width: '100%', 
            maxHeight: '80vh', 
            overflow: 'auto',
            margin: 0
          }}>
            <h3 style={{ marginBottom: '20px', color: 'var(--tropical-deep)' }}>
              Preview Import ({previewData.length} fish)
            </h3>
            
            <div style={{ 
              maxHeight: '400px', 
              overflow: 'auto', 
              border: '1px solid #ddd',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: '#f5f5f5', position: 'sticky', top: 0 }}>
                  <tr>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Name</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Price</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Care Level</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((fish, index) => (
                    <tr key={index}>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{fish.name}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>${fish.price}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{fish.care_level}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{fish.stock_quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowPreview(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleImport(previewData)}
                className="btn"
              >
                Confirm Import ({previewData.length} fish)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {importResults && (
        <div style={{
          padding: '15px',
          borderRadius: '8px',
          background: importResults.success ? 'rgba(85, 239, 196, 0.1)' : 'rgba(253, 121, 168, 0.1)',
          border: `1px solid ${importResults.success ? 'var(--tropical-lime)' : 'var(--tropical-coral)'}`,
          marginTop: '15px'
        }}>
          {importResults.success ? (
            <div>
              <h4 style={{ color: 'var(--tropical-lime)', marginBottom: '8px' }}>
                ✅ Import Successful!
              </h4>
              <p>Successfully imported {importResults.count} fish to your inventory.</p>
            </div>
          ) : (
            <div>
              <h4 style={{ color: 'var(--tropical-coral)', marginBottom: '8px' }}>
                ❌ Import Failed
              </h4>
              <p>Error: {importResults.error}</p>
            </div>
          )}
        </div>
      )}

      {/* Instructions */}
      <div style={{ 
        padding: '15px',
        background: 'rgba(253, 203, 110, 0.1)',
        borderRadius: '8px',
        borderLeft: '4px solid var(--tropical-sun)',
        marginTop: '20px'
      }}>
        <h4 style={{ color: 'var(--tropical-deep)', marginBottom: '10px' }}>🌐 Supported HTML Formats:</h4>
        
        <div style={{ marginBottom: '15px' }}>
          <h5 style={{ color: 'var(--tropical-deep)', marginBottom: '8px' }}>🐠 Marine Fish Care Guide Format:</h5>
          <ul style={{ fontSize: '14px', lineHeight: '1.6', color: '#666', margin: 0, paddingLeft: '20px' }}>
            <li>Uses <code>article.care-card</code> containers for each fish</li>
            <li>Fish name and scientific name in <code>header h2</code> and <code>small</code></li>
            <li>Category in <code>.category</code> class</li>
            <li>Automatic price generation based on category and size</li>
            <li>Extracts tank size, diet, temperament from sections</li>
          </ul>
        </div>

        <div>
          <h5 style={{ color: 'var(--tropical-deep)', marginBottom: '8px' }}>🔧 Standard Format:</h5>
          <ul style={{ fontSize: '14px', lineHeight: '1.6', color: '#666', margin: 0, paddingLeft: '20px' }}>
            <li>Each fish must be in a container with class <code>fish-item</code></li>
            <li>Use class names like <code>fish-name</code>, <code>price</code>, <code>care-level</code>, etc.</li>
            <li>Boolean fields should contain 'true' or 'false' text</li>
            <li>Price should include the dollar amount (e.g., $89.99)</li>
            <li>Images should have class <code>fish-image</code> with src attribute</li>
          </ul>
        </div>

        <div style={{ 
          marginTop: '12px', 
          padding: '10px', 
          background: 'rgba(101, 223, 246, 0.1)', 
          borderRadius: '6px',
          fontSize: '13px',
          color: '#555'
        }}>
          💡 <strong>Tip:</strong> Simply paste your Marine Fish Care Guide HTML content and the system will automatically detect and parse it!
        </div>
      </div>
    </div>
  );
};

export default HtmlImport;
